/**
 * Shared iCarsoft comparison specifications.
 *
 * This file is intentionally read-only data + pure helpers. It does not touch
 * cart, checkout, currency, analytics, app embeds or product forms.
 */
(() => {
  if (window.ICARSOFT_COMPARE_SPECS) return;

  const DASH = '-';

  const clean = (value) => String(value || '').trim();
  const normalize = (value) => clean(value)
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const handleFrom = (item) => {
    if (!item) return '';
    if (item.handle) return normalize(item.handle);
    const url = clean(item.url);
    const match = url.match(/\/products\/([^/?#]+)/i);
    if (match) return normalize(match[1]);
    return normalize(item.title);
  };

  const titleIncludes = (item, needle) => clean(item && item.title).toLowerCase().includes(needle);

  const base = {
    productType: 'Diagnostic tool',
    bestFor: 'Confirm exact fit by VIN before ordering',
    coverage: 'Coverage varies by vehicle',
    depth: 'Confirm supported systems by model year and installed modules',
    screen: 'See product page',
    connection: 'OBD connection',
    updates: 'See product page',
    updateMethod: 'See product page',
    bidirectional: 'Confirm by vehicle',
    activeTests: 'Confirm by vehicle',
    serviceFunctions: 'Confirm by vehicle',
    ecuCoding: 'Confirm by vehicle',
    programming: 'Confirm by vehicle',
    j2534: 'Confirm by vehicle',
    topology: 'Confirm by vehicle',
    tpms: 'Confirm by vehicle',
    hardware: 'Scanner package contents vary by product',
    accessories: 'Optional accessories sold separately',
    highlights: 'Confirm exact compatibility by VIN',
    note: 'Functions vary by make, model year, VIN and installed control modules.'
  };

  const noAdvanced = {
    bidirectional: 'No',
    activeTests: 'No',
    ecuCoding: 'No',
    programming: 'No',
    j2534: 'No',
    topology: 'No'
  };

  const pcUpdate = 'PC / USB update';
  const wifiUpdate = 'Wi-Fi / online update';
  const lifetimeWifi = 'Lifetime Wi-Fi updates';
  const lifetimePc = 'Lifetime PC updates';

  const S = {
    'i800': {
      productType: 'Entry OBDII scanner',
      bestFor: 'Basic check-engine diagnostics',
      coverage: '1996+ OBDII / EOBD / CAN passenger vehicles',
      depth: 'Engine / OBDII only',
      screen: 'Handheld button-control unit',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      serviceFunctions: 'No reset/service package',
      tpms: 'No',
      hardware: 'Handheld scanner with OBD cable',
      highlights: 'Read and clear engine fault codes; live data',
      ...noAdvanced
    },
    'i820': {
      productType: 'Entry OBDII scanner',
      bestFor: 'Budget owner diagnostics',
      coverage: '1996+ OBDII / EOBD / CAN passenger vehicles',
      depth: 'Engine / OBDII diagnostics',
      screen: 'Handheld button-control unit',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      serviceFunctions: 'Limited; see product page',
      tpms: 'No',
      hardware: 'Handheld scanner with OBD cable',
      highlights: 'Engine codes, readiness and live data',
      ...noAdvanced
    },
    'i610': {
      productType: 'Wi-Fi OBDII engine scanner',
      bestFor: 'Phone-based check-engine diagnostics',
      coverage: '1996+ OBDII / EOBD / CAN passenger vehicles',
      depth: 'Engine / OBDII only',
      screen: 'Uses phone / app display',
      connection: 'Wi-Fi OBDII adapter',
      updates: 'App / software dependent',
      updateMethod: 'Mobile app / software update',
      serviceFunctions: 'No reset/service package',
      tpms: 'No',
      hardware: 'Wi-Fi OBDII adapter',
      highlights: 'Wireless engine-code reading and clearing',
      ...noAdvanced
    },
    'i620': {
      productType: 'Bluetooth OBDII engine scanner',
      bestFor: 'Phone-based check-engine diagnostics',
      coverage: '1996+ OBDII / EOBD / CAN passenger vehicles',
      depth: 'Engine / OBDII only',
      screen: 'Uses phone / app display',
      connection: 'Bluetooth OBDII adapter',
      updates: 'App / software dependent',
      updateMethod: 'Mobile app / software update',
      serviceFunctions: 'No reset/service package',
      tpms: 'No',
      hardware: 'Bluetooth OBDII adapter',
      highlights: 'Bluetooth engine-code reading and clearing',
      ...noAdvanced
    },
    'icarsoft-e660': {
      productType: '4-system diagnostic tool',
      bestFor: 'Everyday owner and DIY checks',
      coverage: '58 worldwide makes',
      depth: 'Engine, ABS, SRS and transmission',
      screen: 'Handheld button-control unit',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      serviceFunctions: 'Selected reset functions',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Handheld scanner with OBD cable',
      highlights: '4 core systems with essential resets',
      ...noAdvanced
    },
    'cr-plus-new': {
      productType: '4-system diagnostic tool',
      bestFor: 'DIY users needing core systems',
      coverage: '58 worldwide makes',
      depth: 'Engine, ABS, SRS and transmission',
      screen: 'Handheld button-control unit',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      serviceFunctions: 'Essential reset functions',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Handheld scanner with OBD cable',
      highlights: 'Core-system diagnostics and resets',
      ...noAdvanced
    },
    'cr-elite': {
      productType: '4-system diagnostic tool',
      bestFor: 'Everyday diagnostics plus service resets',
      coverage: '58 worldwide makes',
      depth: '4 core systems with selected special functions',
      screen: 'Handheld button-control unit',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      bidirectional: 'No',
      activeTests: 'No',
      serviceFunctions: 'Selected reset functions',
      ecuCoding: 'No',
      programming: 'No',
      j2534: 'No',
      topology: 'No',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Handheld scanner with OBD cable',
      highlights: 'Core systems and service maintenance'
    },
    'cr-genius': {
      productType: 'VIN scan and service reset tool',
      bestFor: 'DIY users needing Auto VIN and maintenance reset functions',
      coverage: 'Passenger vehicles; exact support varies by VIN',
      depth: 'OBD diagnostics plus selected service/relearn functions',
      screen: 'Handheld / tablet-style diagnostic tool',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      bidirectional: 'Confirm by vehicle',
      activeTests: 'Confirm by vehicle',
      serviceFunctions: 'Oil, EPB, SAS and other relearn/reset functions vary by vehicle',
      ecuCoding: 'No / confirm by vehicle',
      programming: 'No J2534 passthrough',
      j2534: 'No',
      topology: 'No',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Auto VIN diagnostic scanner with OBD cable',
      highlights: 'Auto VIN scan, maintenance resets and relearn procedures',
      note: 'Confirm VIN, model year and required reset functions before ordering.'
    },
    'icarsoft-cr-elite-p': {
      productType: 'Advanced 4-system tablet',
      bestFor: 'Advanced DIY users wanting touchscreen and Wi-Fi',
      coverage: '58 worldwide makes',
      depth: '4 core systems plus advanced special functions',
      screen: '5-inch touchscreen',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: '50 service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'No J2534 passthrough',
      j2534: 'No',
      topology: 'No',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Touchscreen scanner with OBD cable',
      highlights: 'Touchscreen, Wi-Fi updates, active tests and service functions'
    },
    'icarsoft-cr-pro': {
      productType: 'All-system handheld scanner',
      bestFor: 'DIY and enthusiast diagnostics',
      coverage: '58 worldwide makes',
      depth: 'All-system diagnostics',
      screen: '4.3-inch button-control screen',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: '40+ service functions',
      ecuCoding: 'Limited / confirm by vehicle',
      programming: 'No J2534 passthrough',
      j2534: 'No',
      topology: 'No',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Handheld scanner with OBD cable',
      highlights: 'All-system scan, active tests and service resets'
    },
    'cr-pro': {
      productType: 'All-system handheld scanner',
      bestFor: 'DIY and enthusiast diagnostics',
      coverage: '58 worldwide makes',
      depth: 'All-system diagnostics',
      screen: 'Button-control handheld unit',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      bidirectional: 'Limited / confirm by vehicle',
      activeTests: 'Limited / confirm by vehicle',
      serviceFunctions: 'Essential service functions',
      ecuCoding: 'No',
      programming: 'No',
      j2534: 'No',
      topology: 'No',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Handheld scanner with OBD cable',
      highlights: 'All-system scan and essential resets'
    },
    'icarsoft-cr-pro-s': {
      productType: 'All-system touchscreen scanner',
      bestFor: 'DIY users and mobile technicians',
      coverage: '58 worldwide makes',
      depth: 'All-system diagnostics',
      screen: '5-inch touchscreen',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: '40+ service functions',
      ecuCoding: 'Limited / confirm by vehicle',
      programming: 'No J2534 passthrough',
      j2534: 'No',
      topology: 'No',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Touchscreen scanner with OBD cable',
      highlights: 'Touchscreen, all-system diagnostics and service resets'
    },
    'cr-max': {
      productType: 'All-system diagnostic tablet',
      bestFor: 'Workshops and advanced DIY',
      coverage: '58 worldwide makes',
      depth: 'All-system diagnostics and OE-level workflows',
      screen: '7-inch touchscreen',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: '50 service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'No J2534 passthrough',
      j2534: 'No',
      topology: 'No',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Tablet with OBD cable',
      highlights: 'Bi-directional controls, ECU coding and 50 services'
    },
    'cr-max-bt': {
      productType: 'Wireless all-system tablet',
      bestFor: 'Wireless diagnostics for advanced DIY and workshops',
      coverage: '58 worldwide makes',
      depth: 'All-system diagnostics and OE-level workflows',
      screen: '7-inch touchscreen',
      connection: 'Wireless Bluetooth VCI',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: '50 service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'No J2534 passthrough',
      j2534: 'No',
      topology: 'No',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Tablet plus Bluetooth VCI',
      highlights: 'Wireless active tests, ECU coding and service functions'
    },
    'icarsoft-cr-max-bt-enhanced-wireless-bidirectional-diagnostic-tool': {
      productType: 'Wireless all-system tablet kit',
      bestFor: 'Advanced DIY and workshops needing OBDI adapters',
      coverage: 'Worldwide makes',
      depth: 'All-system diagnostics and enhanced active tests',
      screen: '7-inch touchscreen',
      connection: 'Wireless Bluetooth VCI plus adapters',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: '50 service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'No J2534 passthrough',
      j2534: 'No',
      topology: 'No',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Tablet, Bluetooth VCI and adapter kit',
      highlights: 'Enhanced wireless package with OBDI adapter support'
    },
    'icarsoft-cr-max-p': {
      productType: 'Pro bi-directional tablet',
      bestFor: 'Advanced DIY and daily workshop diagnostics',
      coverage: '58 worldwide makes',
      depth: 'All-system OE-level diagnostics',
      screen: '10-inch touchscreen',
      connection: 'Wireless Bluetooth VCI',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: '50 service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'J2534-ready workflows on supported vehicles',
      j2534: 'Supported / J2534-ready',
      topology: 'Supported on compatible vehicles',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Tablet plus Bluetooth VCI',
      highlights: 'Bi-directional control, ECU coding and J2534-ready workflows'
    },
    'cr-eagle-p': {
      productType: '2026 flagship diagnostic tablet',
      bestFor: 'Advanced DIY and workshops needing TPMS plus all-system coverage',
      coverage: '58 worldwide makes',
      depth: 'All-system OE-level diagnostics',
      screen: '10.1-inch touchscreen',
      connection: 'Wireless Bluetooth VCI',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: '50 service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'J2534-ready workflows on supported vehicles',
      j2534: 'Supported / J2534-ready',
      topology: 'Supported on compatible vehicles',
      tpms: 'TPMS programming including supported sensors',
      hardware: 'Tablet, Bluetooth VCI and TPMS accessories package',
      highlights: 'TPMS programming, ECU coding, active tests and J2534-ready workflows'
    },
    'icarsoft-cr-eagle': {
      productType: 'Professional diagnostic tablet',
      bestFor: 'Professional workshops needing broad all-system coverage',
      coverage: '58 worldwide makes',
      depth: 'All-system OE-level diagnostics',
      screen: '10.1-inch touchscreen',
      connection: 'Wireless Bluetooth VCI',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: '50 service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'J2534-ready workflows on supported vehicles',
      j2534: 'Supported / J2534-ready',
      topology: 'Supported on compatible vehicles',
      tpms: 'TPMS programming supported on compatible vehicles',
      hardware: 'Tablet plus Bluetooth VCI',
      highlights: 'ECU coding, TPMS programming, active tests and J2534-ready workflows'
    },
    'cr-legend': {
      productType: 'Professional all-system platform',
      bestFor: 'Workshops needing broad vehicle-network coverage',
      coverage: 'Worldwide makes with CAN FD / DoIP support',
      depth: 'All-system OE-level diagnostics',
      screen: 'Touchscreen tablet',
      connection: 'Wireless VCI / OBD connection',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: '50 service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'Advanced programming workflows vary by vehicle',
      j2534: 'Supported on compatible vehicles',
      topology: 'Supported on compatible vehicles',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Professional tablet package',
      highlights: 'CAN FD, DoIP, ECU coding and broad professional coverage'
    },
    'cr-unicorn': {
      productType: 'OE-level professional platform',
      bestFor: 'Passenger-car workshops needing advanced diagnostics',
      coverage: 'Passenger-car professional coverage',
      depth: 'OE-level diagnostics and advanced workshop functions',
      screen: 'Professional tablet platform',
      connection: 'VCI / OBD connection',
      updates: 'Online platform updates',
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: 'Advanced workshop service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'Programming workflows vary by vehicle',
      j2534: 'Supported on compatible vehicles',
      topology: 'Supported on compatible vehicles',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Professional tablet and VCI package',
      highlights: 'OE-level diagnostics, coding and workshop service functions'
    },
    'icarsoft-cr-unicorn-for-passenger-cars': {
      productType: 'Car and truck professional platform',
      bestFor: 'Workshops servicing passenger cars and supported heavy-duty vehicles',
      coverage: 'Passenger cars plus supported heavy-duty trucks',
      depth: 'OE-level diagnostics and advanced workshop functions',
      screen: 'Professional tablet platform',
      connection: 'VCI / OBD connection',
      updates: 'Online platform updates',
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: 'Advanced workshop service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'Programming workflows vary by vehicle',
      j2534: 'Supported on compatible vehicles',
      topology: 'Supported on compatible vehicles',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Professional tablet and VCI package',
      highlights: 'Passenger-car diagnostics with supported heavy-duty coverage'
    },
    'cr-ultra': {
      productType: 'Professional workshop tablet',
      bestFor: 'Advanced diagnostics and coding workflows',
      coverage: 'OE-level professional coverage',
      depth: 'OE-level diagnostics for supported makes',
      screen: 'Professional touchscreen tablet',
      connection: 'VCI / OBD connection',
      updates: 'USB / online updates',
      updateMethod: 'USB / online update',
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: 'Advanced service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'Programming workflows vary by vehicle',
      j2534: 'Supported on compatible vehicles',
      topology: 'Supported on compatible vehicles',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Professional tablet and VCI package',
      highlights: 'Advanced diagnostics, coding and professional service functions'
    },
    'cr-ultra-p': {
      productType: 'Professional programming platform',
      bestFor: 'Workshops needing topology, J2534 and programming workflows',
      coverage: 'OE-level professional coverage',
      depth: 'OE-level diagnostics and programming workflows',
      screen: 'Professional touchscreen tablet',
      connection: 'VCI / OBD connection',
      updates: '3-year free updates',
      updateMethod: 'Online / USB update',
      bidirectional: 'Supported on compatible vehicles',
      activeTests: 'Supported on compatible vehicles',
      serviceFunctions: 'Advanced service functions',
      ecuCoding: 'Supported on compatible vehicles',
      programming: 'J2534 and programming workflows on supported vehicles',
      j2534: 'Supported',
      topology: 'Supported',
      tpms: 'TPMS service varies by vehicle',
      hardware: 'Professional tablet, VCI and programming package',
      highlights: 'Topology mapping, J2534 and programming workflows'
    },
    'professional-multi-systems-motorcycle-diagnostic-tool-mt-pro': {
      productType: 'Professional multi-brand motorcycle scanner',
      bestFor: 'Motorcycle workshops servicing many makes',
      coverage: '89 motorcycle brands',
      depth: 'Full-system motorcycle diagnostics',
      screen: '7-inch touchscreen',
      connection: 'Motorcycle adapter cable set',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible motorcycles',
      activeTests: 'Supported on compatible motorcycles',
      serviceFunctions: 'Motorcycle service functions vary by make',
      ecuCoding: 'Confirm by motorcycle make',
      programming: 'Confirm by motorcycle make',
      j2534: 'No',
      topology: 'No',
      tpms: 'No passenger-car TPMS programming',
      hardware: 'MT Pro tablet plus motorcycle adapter kit',
      highlights: '89-brand motorcycle coverage and workshop diagnostics'
    },
    'mt-pro-mini': {
      productType: 'Compact multi-brand motorcycle scanner',
      bestFor: 'Riders and small shops needing touchscreen MT coverage',
      coverage: '21 motorcycle brands',
      depth: 'Full-system motorcycle diagnostics',
      screen: '5-inch touchscreen',
      connection: 'Motorcycle adapter cable set',
      updates: lifetimeWifi,
      updateMethod: wifiUpdate,
      bidirectional: 'Supported on compatible motorcycles',
      activeTests: 'Supported on compatible motorcycles',
      serviceFunctions: 'Motorcycle service functions vary by make',
      ecuCoding: 'Confirm by motorcycle make',
      programming: 'Confirm by motorcycle make',
      j2534: 'No',
      topology: 'No',
      tpms: 'No passenger-car TPMS programming',
      hardware: 'MT Pro Mini scanner plus motorcycle adapter kit',
      highlights: 'Compact touchscreen with multi-brand motorcycle coverage'
    },
    'mt-v6-plus': {
      productType: 'Multi-brand motorcycle scanner',
      bestFor: 'Riders needing essential multi-brand motorcycle diagnostics',
      coverage: '21 motorcycle brands',
      depth: 'Essential motorcycle system diagnostics',
      screen: 'Button-control handheld unit',
      connection: '16-adapter motorcycle cable kit',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      bidirectional: 'Confirm by motorcycle make',
      activeTests: 'Confirm by motorcycle make',
      serviceFunctions: 'Motorcycle service functions vary by make',
      ecuCoding: 'No',
      programming: 'No',
      j2534: 'No',
      topology: 'No',
      tpms: 'No passenger-car TPMS programming',
      hardware: 'MT V6 Plus scanner plus 16 motorcycle adapters',
      highlights: 'Essential multi-brand motorcycle diagnostics'
    },
    'icarsoft-hd-v4-0-multi-system-heavy-duty-vehicles-diagnostic-tool': {
      productType: 'Dedicated heavy-duty truck scanner',
      bestFor: '12V and 24V heavy-duty trucks and commercial vehicles',
      coverage: 'Supported heavy-duty trucks, buses and commercial vehicles',
      depth: 'Multi-system heavy-duty diagnostics',
      screen: 'Handheld V4.0 platform',
      connection: 'Heavy-duty connector package; confirm protocol and connector',
      updates: 'Wi-Fi software updates',
      updateMethod: wifiUpdate,
      bidirectional: 'Confirm by truck protocol',
      activeTests: 'Confirm by truck protocol',
      serviceFunctions: 'Heavy-duty service functions vary by vehicle',
      ecuCoding: 'No passenger-car ECU coding',
      programming: 'No passenger-car programming',
      j2534: 'No',
      topology: 'No',
      tpms: 'No passenger-car TPMS programming',
      hardware: 'HD V4.0 scanner and heavy-duty connector package',
      highlights: 'Multi-system commercial vehicle diagnostics'
    },
    'hd-v3-0': {
      productType: 'Heavy-duty code reader',
      bestFor: 'Quick truck fault-code diagnostics',
      coverage: 'Supported heavy-duty trucks; choose the required package and connector',
      depth: 'Heavy-duty fault-code diagnostics',
      screen: 'Handheld button-control unit',
      connection: 'Heavy-duty connector package',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      bidirectional: 'No',
      activeTests: 'No',
      serviceFunctions: 'Limited; see product page',
      ecuCoding: 'No',
      programming: 'No',
      j2534: 'No',
      topology: 'No',
      tpms: 'No passenger-car TPMS programming',
      hardware: 'HD V3.0 scanner and connector package',
      highlights: 'Portable heavy-duty code reader'
    },
    'hd6000': {
      productType: 'Heavy-duty expansion package',
      bestFor: 'CR Ultra users adding heavy-duty truck adapter coverage',
      coverage: 'Heavy-duty adapter package for supported host tools',
      depth: 'Expansion package; functions depend on the host scanner',
      screen: 'No screen - accessory kit',
      connection: 'Commercial-vehicle adapter set',
      updates: 'Uses host scanner updates',
      updateMethod: 'Update through supported host scanner',
      bidirectional: 'Depends on host scanner and truck protocol',
      activeTests: 'Depends on host scanner and truck protocol',
      serviceFunctions: 'Depends on host scanner and truck protocol',
      ecuCoding: 'Depends on host scanner and vehicle',
      programming: 'Depends on host scanner and vehicle',
      j2534: 'Depends on host scanner',
      topology: 'Depends on host scanner',
      tpms: 'No passenger-car TPMS programming',
      hardware: 'Heavy-duty adapter package',
      highlights: 'Multi-connector heavy-duty adapter package'
    },
    'hd2000-heavy-duty-truck-package': {
      productType: 'Heavy-duty expansion interface',
      bestFor: 'CR Ultra users adding heavy-duty communication interface support',
      coverage: 'Heavy-duty truck package for supported host tools',
      depth: 'Expansion interface; functions depend on the host scanner',
      screen: 'No screen - interface module',
      connection: 'HD2000 communication interface',
      updates: 'Uses host scanner updates',
      updateMethod: 'Update through supported host scanner',
      bidirectional: 'Depends on host scanner and truck protocol',
      activeTests: 'Depends on host scanner and truck protocol',
      serviceFunctions: 'Depends on host scanner and truck protocol',
      ecuCoding: 'Depends on host scanner and vehicle',
      programming: 'Depends on host scanner and vehicle',
      j2534: 'Depends on host scanner',
      topology: 'Depends on host scanner',
      tpms: 'No passenger-car TPMS programming',
      hardware: 'HD2000 interface module and truck package',
      highlights: 'Heavy-duty communication expansion for CR Ultra'
    },
    'marinescan-x9': {
      productType: 'Marine diagnostic scanner',
      bestFor: 'Marine engine diagnostic workflows',
      coverage: 'Supported marine engines',
      depth: 'Marine system diagnostics',
      screen: 'Handheld marine scanner',
      connection: 'Marine diagnostic cable package',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      serviceFunctions: 'Marine service functions vary by engine',
      hardware: 'Marine scanner and cable package',
      highlights: 'Marine engine diagnostics and service workflows'
    },
    'one-year-software-subscription-online-card': {
      productType: 'Software subscription',
      bestFor: 'Renewing eligible scanner software access',
      coverage: 'Eligible iCarsoft platforms only',
      depth: 'Software update access',
      screen: 'Digital / card product',
      connection: 'No vehicle connection',
      updates: 'One-year software subscription',
      updateMethod: 'Online activation',
      hardware: 'Subscription card / activation product',
      highlights: 'Renew supported scanner updates',
      note: 'Confirm your scanner model before purchase.'
    },
    'oscilloscope': {
      productType: 'Workshop module',
      bestFor: 'Signal testing and waveform diagnostics',
      coverage: 'Supported professional iCarsoft platforms',
      depth: '4-channel scope and waveform testing',
      screen: 'Uses host scanner display',
      connection: 'Scope module connection',
      updates: 'Uses host scanner updates',
      updateMethod: 'Through supported host scanner',
      hardware: '4-channel oscilloscope module',
      highlights: 'Waveform testing and signal analysis'
    },
    'vdsp2000': {
      productType: 'Inspection camera',
      bestFor: 'Visual inspection and recording',
      coverage: 'Supported professional iCarsoft platforms',
      depth: 'Inspection accessory',
      screen: 'Uses host scanner display',
      connection: 'Videoscope connection',
      updates: 'Uses host scanner updates',
      updateMethod: 'Through supported host scanner',
      hardware: 'Videoscope camera head',
      highlights: '5.5 mm camera head with adjustable lighting'
    },
    'bt900-battery-test-clip': {
      productType: 'Battery test accessory',
      bestFor: 'Battery testing support',
      coverage: 'Compatible iCarsoft battery-test workflows',
      depth: 'Battery testing accessory',
      screen: 'No screen - clip accessory',
      connection: 'Clip-on battery connection',
      updates: 'No software update required',
      updateMethod: 'Not applicable',
      hardware: 'Battery test clip and cable',
      highlights: 'Clip-on battery connection'
    },
    't-wand-9000': {
      productType: 'TPMS activation tool',
      bestFor: 'TPMS sensor activation and relearn support',
      coverage: 'Compatible TPMS workflows',
      depth: 'TPMS activation accessory',
      screen: 'Handheld TPMS accessory',
      connection: 'TPMS activation interface',
      updates: 'See product page',
      updateMethod: 'See product page',
      tpms: 'TPMS sensor activation and relearn support',
      hardware: 'Standalone handheld TPMS activation tool',
      highlights: 'TPMS sensor activation'
    },
    'tpms-s2000-2-in-1-sensor': {
      productType: 'TPMS sensor',
      bestFor: 'Replacement TPMS sensor service',
      coverage: 'Confirm frequency and vehicle support',
      depth: 'Replacement sensor',
      screen: 'No screen - sensor',
      connection: 'Vehicle TPMS sensor',
      updates: 'No software update required',
      updateMethod: 'Not applicable',
      tpms: '2-in-1 replacement sensor',
      hardware: 'Replacement TPMS sensor',
      highlights: 'Metal clamp-in replacement sensor'
    },
    'tpms-s6000': {
      productType: 'TPMS sensor',
      bestFor: 'Replacement TPMS sensor service',
      coverage: 'Confirm frequency and vehicle support',
      depth: 'Replacement sensor',
      screen: 'No screen - sensor',
      connection: 'Vehicle TPMS sensor',
      updates: 'No software update required',
      updateMethod: 'Not applicable',
      tpms: 'Replacement TPMS sensor',
      hardware: 'Replacement TPMS sensor',
      highlights: 'TPMS replacement sensor'
    },
    'cr-ultra-vci': {
      productType: 'VCI module',
      bestFor: 'Replacing or adding a CR Ultra communication interface',
      coverage: 'CR Ultra / compatible professional platform',
      depth: 'Communication interface',
      screen: 'No screen - VCI module',
      connection: 'Vehicle communication interface',
      updates: 'Uses host scanner updates',
      updateMethod: 'Through supported host scanner',
      hardware: 'CR Ultra VCI module',
      highlights: 'VCI replacement / expansion module'
    },
    'vci-for-cr-max-bt': {
      productType: 'VCI module',
      bestFor: 'Replacing or adding CR MAX BT wireless communication',
      coverage: 'CR MAX BT compatible platform',
      depth: 'Communication interface',
      screen: 'No screen - VCI module',
      connection: 'Wireless vehicle communication interface',
      updates: 'Uses host scanner updates',
      updateMethod: 'Through supported host scanner',
      hardware: 'CR MAX BT VCI module',
      highlights: 'Wireless VCI replacement / expansion module'
    },
    'main-cable': {
      productType: 'Main cable',
      bestFor: 'Replacing the scanner main OBD cable',
      coverage: 'Compatible iCarsoft scanners',
      depth: 'Cable accessory',
      screen: 'No screen - cable',
      connection: 'Main OBD cable',
      updates: 'No software update required',
      updateMethod: 'Not applicable',
      hardware: 'Replacement main cable',
      highlights: 'Main diagnostic cable'
    },
    'tpms-v800': {
      productType: 'TPMS service tool',
      bestFor: 'TPMS diagnostics, sensor activation and relearn workflows',
      coverage: 'TPMS support varies by vehicle and sensor type',
      depth: 'TPMS service and activation workflows',
      screen: 'Handheld TPMS tool',
      connection: 'OBDII plus TPMS sensor activation interface',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      bidirectional: 'No passenger-car all-system active tests',
      activeTests: 'TPMS activation / relearn workflows only',
      serviceFunctions: 'TPMS relearn and service workflows; confirm by vehicle',
      ecuCoding: 'No',
      programming: 'TPMS sensor programming / activation on supported sensors',
      j2534: 'No',
      topology: 'No',
      tpms: 'TPMS activation, relearn and sensor service',
      hardware: 'Handheld TPMS tool with OBD / TPMS service accessories',
      highlights: 'TPMS diagnosis, sensor activation and relearn support',
      note: 'Confirm vehicle year, TPMS sensor type and required relearn procedure before ordering.'
    },
    '20254680-additional-payment': {
      productType: 'Additional payment link',
      bestFor: 'Order balance or custom payment arranged by support',
      coverage: 'Not a diagnostic scanner',
      depth: 'Payment-only item',
      screen: 'Not applicable',
      connection: 'Not applicable',
      updates: 'Not applicable',
      updateMethod: 'Not applicable',
      bidirectional: 'Not applicable',
      activeTests: 'Not applicable',
      serviceFunctions: 'Not applicable',
      ecuCoding: 'Not applicable',
      programming: 'Not applicable',
      j2534: 'Not applicable',
      topology: 'Not applicable',
      tpms: 'Not applicable',
      hardware: 'No hardware included unless confirmed by support',
      highlights: 'Use only when instructed by iCarsoft support',
      note: 'This is not a standalone product. Confirm the order purpose with support before purchase.'
    },
    'additional-payment-of-1953': {
      productType: 'Additional payment link',
      bestFor: 'Order balance or custom payment arranged by support',
      coverage: 'Not a diagnostic scanner',
      depth: 'Payment-only item',
      screen: 'Not applicable',
      connection: 'Not applicable',
      updates: 'Not applicable',
      updateMethod: 'Not applicable',
      bidirectional: 'Not applicable',
      activeTests: 'Not applicable',
      serviceFunctions: 'Not applicable',
      ecuCoding: 'Not applicable',
      programming: 'Not applicable',
      j2534: 'Not applicable',
      topology: 'Not applicable',
      tpms: 'Not applicable',
      hardware: 'No hardware included unless confirmed by support',
      highlights: 'Use only when instructed by iCarsoft support',
      note: 'This is not a standalone product. Confirm the order purpose with support before purchase.'
    }
  };

  const ALIASES = {
    'por-ii-1': 'por-ii',
    'bmw-v3-0': 'bmm-v3-0',
    'mtv6-bmw': 'mt-v6-for-bmw',
    'mt-pro': 'professional-multi-systems-motorcycle-diagnostic-tool-mt-pro',
    'icarsoft-mt-pro': 'professional-multi-systems-motorcycle-diagnostic-tool-mt-pro',
    'icarsoft-mt-pro-motorcycle-diagnostic-tool': 'professional-multi-systems-motorcycle-diagnostic-tool-mt-pro',
    'icarsoft-hd-v4-0': 'icarsoft-hd-v4-0-multi-system-heavy-duty-vehicles-diagnostic-tool',
    'hd-v4-0': 'icarsoft-hd-v4-0-multi-system-heavy-duty-vehicles-diagnostic-tool',
    'icarsoft-hd-v4-0-multi-system-heavy-duty-diagnostic-tool': 'icarsoft-hd-v4-0-multi-system-heavy-duty-vehicles-diagnostic-tool',
    'hd2000': 'hd2000-heavy-duty-truck-package',
    'icarsoft-hd2000': 'hd2000-heavy-duty-truck-package',
    'icarsoft-hd2000-heavy-duty-truck-package-for-cr-ultra': 'hd2000-heavy-duty-truck-package',
    'hd6000-heavy-duty-truck-package': 'hd6000',
    'icarsoft-hd6000': 'hd6000',
    'icarsoft-hd6000-heavy-duty-truck-package': 'hd6000',
    'icarsoft-one-year-software-subscription-card': 'one-year-software-subscription-online-card',
    'one-year-software-subscription-card': 'one-year-software-subscription-online-card',
    'software-subscription-card': 'one-year-software-subscription-online-card',
    'scope-box': 'oscilloscope',
    'icarsoft-scope-box': 'oscilloscope',
    'icarsoft-scope-box-4-channel-oscilloscope-module': 'oscilloscope',
    'oscilloscope-module': 'oscilloscope',
    'videoscope': 'vdsp2000',
    'video-scope': 'vdsp2000',
    'icarsoft-vdsp2000': 'vdsp2000',
    'bt900': 'bt900-battery-test-clip',
    'icarsoft-bt900': 'bt900-battery-test-clip',
    't-wand': 't-wand-9000',
    't-wand-9000-tpms-activation-tool': 't-wand-9000',
    'icarsoft-t-wand-9000': 't-wand-9000',
    'icarsoft-t-wand-9000-tpms-activation-tool': 't-wand-9000',
    's2000': 'tpms-s2000-2-in-1-sensor',
    'tpms-s2000': 'tpms-s2000-2-in-1-sensor',
    'icarsoft-s2000': 'tpms-s2000-2-in-1-sensor',
    'icarsoft-tpms-s2000-2-in-1-sensor': 'tpms-s2000-2-in-1-sensor',
    'v800': 'tpms-v800',
    'icarsoft-v800': 'tpms-v800'
  };

  const adapterSpec = (handle) => {
    if (/screen-protector|tempered-glass/.test(handle)) {
      return {
        productType: 'Screen protector',
        bestFor: 'Protecting compatible iCarsoft scanner displays',
        coverage: 'Compatible scanner model family',
        depth: 'Protection accessory',
        screen: 'Screen protection',
        connection: 'No vehicle connection',
        updates: 'No software update required',
        updateMethod: 'Not applicable',
        hardware: 'Tempered-glass screen protector',
        highlights: 'Display protection accessory'
      };
    }
    if (/adapter|pin|audi-2x2|cable|conector|connector/.test(handle)) {
      return {
        productType: 'Adapter / cable',
        bestFor: 'Connecting supported older or brand-specific diagnostic ports',
        coverage: 'Brand-specific connector support',
        depth: 'Connection accessory',
        screen: 'No screen - cable / adapter',
        connection: 'Vehicle diagnostic adapter',
        updates: 'No software update required',
        updateMethod: 'Not applicable',
        hardware: 'Adapter cable',
        highlights: 'Port adapter for compatible iCarsoft scanners',
        note: 'Confirm scanner compatibility and vehicle connector before ordering.'
      };
    }
    return null;
  };

  const singleBrandSpec = (handle) => {
    const brand = handle
      .replace(/^icarsoft-/, '')
      .replace(/-enhanced-edition$/, '')
      .replace(/-v[0-9]-0.*$/, '')
      .replace(/-ii.*$/, '')
      .replace(/-/g, ' ')
      .toUpperCase();
    if (/v4-0/.test(handle)) {
      return {
        productType: 'Single-brand V4.0 scanner',
        bestFor: `Focused ${brand} vehicle diagnostics`,
        coverage: 'Preloaded specialist vehicle-brand family coverage',
        depth: 'Full-system diagnostics with advanced service functions',
        screen: '5-inch touchscreen',
        connection: 'Wired OBDII 16-pin',
        updates: lifetimeWifi,
        updateMethod: wifiUpdate,
        bidirectional: 'Supported on compatible vehicles',
        activeTests: 'Supported on compatible vehicles',
        serviceFunctions: '40+ service functions; varies by make',
        ecuCoding: 'Supported on compatible vehicles',
        programming: 'No J2534 passthrough',
        j2534: 'No',
        topology: 'No',
        tpms: 'TPMS service varies by vehicle',
        hardware: 'Single-brand V4.0 scanner with OBD cable',
        highlights: 'Touchscreen, Wi-Fi updates, full-system diagnostics'
      };
    }
    if (/v3-0/.test(handle)) {
      return {
        productType: 'Single-brand V3.0 scanner',
        bestFor: `Advanced ${brand} diagnostics`,
        coverage: 'Preloaded specialist vehicle-brand family coverage',
        depth: 'Full-system diagnostics with active tests on compatible vehicles',
        screen: '4.3-inch button-control screen',
        connection: 'Wired OBDII 16-pin',
        updates: lifetimePc,
        updateMethod: pcUpdate,
        bidirectional: 'Supported on compatible vehicles',
        activeTests: 'Supported on compatible vehicles',
        serviceFunctions: '30+ / 40+ service functions; varies by make',
        ecuCoding: 'Supported on compatible vehicles',
        programming: 'No J2534 passthrough',
        j2534: 'No',
        topology: 'No',
        tpms: 'TPMS service varies by vehicle',
        hardware: 'Single-brand V3.0 scanner with OBD cable',
        highlights: 'Full-system diagnostics, active tests and service functions'
      };
    }
    if (/v2-0/.test(handle)) {
      return {
        productType: 'Single-brand V2.0 scanner',
        bestFor: `Everyday ${brand} diagnostics`,
        coverage: 'Preloaded specialist vehicle-brand family coverage',
        depth: 'Full-system diagnostics with essential reset functions',
        screen: '4-inch button-control screen',
        connection: 'Wired OBDII 16-pin',
        updates: lifetimePc,
        updateMethod: pcUpdate,
        bidirectional: 'No / limited; confirm by vehicle',
        activeTests: 'No / limited; confirm by vehicle',
        serviceFunctions: 'Essential service reset functions',
        ecuCoding: 'No / limited; confirm by vehicle',
        programming: 'No',
        j2534: 'No',
        topology: 'No',
        tpms: 'TPMS service varies by vehicle',
        hardware: 'Single-brand V2.0 scanner with OBD cable',
        highlights: 'Full-system scan and essential service resets'
      };
    }
    if (/v1-0|ii|i900|i902|i906|i980|p700/.test(handle)) {
      return {
        productType: 'Single-brand entry scanner',
        bestFor: `Essential ${brand} diagnostics`,
        coverage: 'Preloaded specialist vehicle-brand family coverage',
        depth: 'Essential system diagnostics',
        screen: 'Button-control handheld unit',
        connection: 'Wired OBDII 16-pin',
        updates: lifetimePc,
        updateMethod: pcUpdate,
        serviceFunctions: 'Basic maintenance functions vary by model',
        tpms: 'Confirm by vehicle',
        hardware: 'Single-brand handheld scanner with OBD cable',
        highlights: 'Essential diagnostics for one vehicle-brand family',
        ...noAdvanced
      };
    }
    return null;
  };

  const motorcycleSingleSpec = (handle) => {
    if (!/^mt-v6|^icarsoft-mt-v6|^mtv6|^icarsoft-motorcycle-diagnostic-tool-mt-v6/.test(handle)) return null;
    return {
      productType: 'Single-brand motorcycle scanner',
      bestFor: 'One motorcycle brand / rider maintenance',
      coverage: 'Dedicated MT V6 motorcycle brand coverage',
      depth: 'Motorcycle system diagnostics',
      screen: 'Handheld button-control unit',
      connection: 'Dedicated motorcycle connector package',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      bidirectional: 'Confirm by motorcycle model',
      activeTests: 'Confirm by motorcycle model',
      serviceFunctions: 'Motorcycle service functions vary by make',
      ecuCoding: 'No / confirm by motorcycle model',
      programming: 'No',
      j2534: 'No',
      topology: 'No',
      tpms: 'No passenger-car TPMS programming',
      hardware: 'MT V6 scanner and motorcycle adapter cable',
      highlights: 'Dedicated single-brand motorcycle diagnostics'
    };
  };

  const regionalMultiBrandSpec = (handle) => {
    const region = handle.startsWith('eu-') ? 'European' :
      handle.startsWith('jp-') ? 'Japanese / Korean' :
      handle.startsWith('us-') || handle.startsWith('fd-') ? 'American' :
      handle.startsWith('fr-') || handle.startsWith('cp-') ? 'French / Italian' : '';
    if (!region) return null;
    if (/-max$/.test(handle) || /v3-0/.test(handle)) {
      return {
        productType: `${region} all-system scanner`,
        bestFor: `${region} vehicle diagnostics with active tests`,
        coverage: `${region} vehicle makes`,
        depth: 'All-system diagnostics',
        screen: '4.3-inch button-control screen',
        connection: 'Wired OBDII 16-pin',
        updates: lifetimePc,
        updateMethod: pcUpdate,
        bidirectional: 'Supported on compatible vehicles',
        activeTests: 'Supported on compatible vehicles',
        serviceFunctions: '30+ / 40+ service functions; varies by make',
        ecuCoding: 'Supported on compatible vehicles',
        programming: 'No J2534 passthrough',
        j2534: 'No',
        topology: 'No',
        tpms: 'TPMS service varies by vehicle',
        hardware: `${region} scanner with OBD cable`,
        highlights: 'Regional multi-make diagnostics and service functions'
      };
    }
    return {
      productType: `${region} multi-make scanner`,
      bestFor: `${region} vehicle owners`,
      coverage: `${region} vehicle makes`,
      depth: 'Full-system diagnostics with essential resets',
      screen: 'Button-control handheld unit',
      connection: 'Wired OBDII 16-pin',
      updates: lifetimePc,
      updateMethod: pcUpdate,
      serviceFunctions: 'Essential service reset functions',
      tpms: 'TPMS service varies by vehicle',
      hardware: `${region} scanner with OBD cable`,
      highlights: 'Regional multi-make diagnostics',
      ...noAdvanced
    };
  };

  const crSeriesSpec = (handle) => {
    if (handle === 'cr-v3-0') {
      return {
        productType: 'Selectable-group all-system scanner',
        bestFor: 'Users servicing several selected vehicle groups',
        coverage: '10 selectable vehicle groups',
        depth: 'All-system diagnostics',
        screen: '5-inch touchscreen',
        connection: 'Wired OBDII 16-pin',
        updates: lifetimeWifi,
        updateMethod: wifiUpdate,
        bidirectional: 'Supported on compatible vehicles',
        activeTests: 'Supported on compatible vehicles',
        serviceFunctions: '40+ service functions',
        ecuCoding: 'Supported on compatible vehicles',
        programming: 'No J2534 passthrough',
        j2534: 'No',
        topology: 'No',
        tpms: 'TPMS service varies by vehicle',
        hardware: 'Scanner with OBD cable',
        highlights: '10 selected groups, touchscreen and Wi-Fi updates'
      };
    }
    if (handle === 'cr-v2-0') {
      return {
        productType: 'Selectable-group all-system scanner',
        bestFor: 'DIY users servicing several selected vehicle groups',
        coverage: '10 selectable vehicle groups',
        depth: 'All-system diagnostics with essential resets',
        screen: 'Button-control handheld unit',
        connection: 'Wired OBDII 16-pin',
        updates: lifetimePc,
        updateMethod: pcUpdate,
        serviceFunctions: 'Essential service reset functions',
        tpms: 'TPMS service varies by vehicle',
        hardware: 'Scanner with OBD cable',
        highlights: '10 selected groups and essential service resets',
        ...noAdvanced
      };
    }
    return null;
  };

  const specFor = (input, context = '') => {
    const item = typeof input === 'string' ? { handle: input } : (input || {});
    const raw = handleFrom(item);
    const handle = ALIASES[raw] || raw;
    const exact = S[handle];
    const spec = exact
      || adapterSpec(handle)
      || motorcycleSingleSpec(handle)
      || crSeriesSpec(handle)
      || regionalMultiBrandSpec(handle)
      || singleBrandSpec(handle)
      || {};

    const merged = { ...base, ...spec };
    if (context === 'accessory') {
      merged.bidirectional = merged.bidirectional === base.bidirectional ? 'Depends on host scanner' : merged.bidirectional;
      merged.serviceFunctions = merged.serviceFunctions === base.serviceFunctions ? 'Depends on host scanner' : merged.serviceFunctions;
    }
    return merged;
  };

  const valueOf = (item, key) => {
    const value = item && item[key];
    return clean(value) || DASH;
  };

  const merge = (item, context = '') => {
    const source = item || {};
    const handle = handleFrom(source);
    const spec = specFor(source, context);
    const merged = { ...spec, ...source, handle };

    const fill = (key, specKey = key) => {
      if (!clean(merged[key])) merged[key] = spec[specKey] || DASH;
    };

    fill('productType');
    fill('bestFor');
    fill('coverage');
    fill('depth');
    fill('screen');
    fill('connection');
    fill('updates');
    fill('updateMethod');
    fill('bidirectional');
    fill('activeTests');
    fill('serviceFunctions');
    fill('ecuCoding');
    fill('programming');
    fill('j2534');
    fill('topology');
    fill('tpms');
    fill('hardware');
    fill('accessories');
    fill('highlights');
    fill('note');
    if (!clean(merged.user)) merged.user = merged.bestFor;
    if (!clean(merged.features)) merged.features = merged.highlights;
    if (!clean(merged.controls)) merged.controls = merged.screen;
    if (!clean(merged.label)) merged.label = merged.productType;
    if (!clean(merged.compatibility)) merged.compatibility = merged.coverage;
    if (!clean(merged.series)) merged.series = merged.productType;
    if (!clean(merged.type)) merged.type = merged.productType;
    if (!clean(merged.description)) merged.description = merged.highlights;
    if (!clean(merged.fullSystem)) merged.fullSystem = /all-system|full-system|oe-level|professional/i.test(`${merged.depth} ${merged.coverage}`) ? 'Yes, varies by vehicle' : 'Limited / see product page';
    return merged;
  };

  const rowsFor = (context = 'catalog') => {
    if (context === 'accessory') {
      return [
        ['Product type', 'productType'],
        ['Price', 'price'],
        ['Works with', 'coverage'],
        ['Hardware', 'hardware'],
        ['Connection', 'connection'],
        ['Update method', 'updateMethod'],
        ['Functions affected', 'depth'],
        ['Highlights', 'highlights'],
        ['Compatibility note', 'note']
      ];
    }
    if (context === 'heavy') {
      return [
        ['Product type', 'productType'],
        ['Price', 'price'],
        ['Truck coverage', 'coverage'],
        ['Diagnostic depth', 'depth'],
        ['Connection / adapters', 'connection'],
        ['Screen / control', 'screen'],
        ['Updates', 'updates'],
        ['Bi-directional tests', 'bidirectional'],
        ['Service functions', 'serviceFunctions'],
        ['Hardware', 'hardware'],
        ['Compatibility note', 'note']
      ];
    }
    if (context === 'motorcycle') {
      return [
        ['Product type', 'productType'],
        ['Price', 'price'],
        ['Motorcycle coverage', 'coverage'],
        ['Diagnostic depth', 'depth'],
        ['Interface / adapters', 'connection'],
        ['Screen / control', 'screen'],
        ['Updates', 'updates'],
        ['Bi-directional tests', 'bidirectional'],
        ['Service functions', 'serviceFunctions'],
        ['Hardware', 'hardware'],
        ['Best for', 'bestFor'],
        ['Compatibility note', 'note']
      ];
    }
    return [
      ['Product type', 'productType'],
      ['Price', 'price'],
      ['Best for', 'bestFor'],
      ['Vehicle coverage', 'coverage'],
      ['Diagnostic depth', 'depth'],
      ['Screen / control', 'screen'],
      ['Connection', 'connection'],
      ['Updates', 'updates'],
      ['Bi-directional tests', 'bidirectional'],
      ['Service functions', 'serviceFunctions'],
      ['ECU coding', 'ecuCoding'],
      ['Programming / J2534', 'programming'],
      ['Topology map', 'topology'],
      ['TPMS', 'tpms'],
      ['Hardware', 'hardware'],
      ['Compatibility note', 'note']
    ];
  };

  window.ICARSOFT_COMPARE_SPECS = {
    merge,
    rowsFor,
    specFor,
    value: valueOf,
    handleFrom
  };
})();
